import React, { useState, useMemo, useEffect } from "react";
import {
    Box,
    Typography,
    TextField,
    MenuItem,
    Select,
    Stack,
    FormControl,
    InputLabel,
} from "@mui/material";
import { nrcdata } from "../../utils/nrcdata";

const NRC_TYPES = [
    { en: "N", mm: "နိုင်" },
    { en: "E", mm: "ဧည့်" },
    { en: "P", mm: "ပြု" },
    { en: "T", mm: "သာသနာ" },
    { en: "R", mm: "ယာယီ" },
    { en: "S", mm: "စ" },
];

const NRC_CODES = Array.from({ length: 14 }, (_, i) => ({
    en: String(i + 1),
    mm: ["၁", "၂", "၃", "၄", "၅", "၆", "၇", "၈", "၉", "၁၀", "၁၁", "၁၂", "၁၃", "၁၄"][i],
}));

const NRCSelector = ({ value, onChange, error, helperText }) => {
    // Current format is usually "12/YAKANA(N)123456"
    // We need to parse this into parts
    const [parts, setParts] = useState({
        code: "12",
        township: "YAKANA",
        type: "N",
        number: ""
    });

    useEffect(() => {
        if (value && typeof value === 'string') {
            // Updated regex to allow 1-6 digits for the number part
            const regex = /^(\d{1,2})\/([A-Za-z]+)\(([N,E,P,T,R,S])\)(\d{1,6})$/;
            const match = value.match(regex);
            if (match) {
                const code = match[1];
                const rawTownship = match[2];
                // Find matching township in data (case-insensitive) to get correct casing
                const matchedTown = nrcdata.find(item => 
                    item.nrc_code === code && 
                    item.name_en.toLowerCase() === rawTownship.toLowerCase()
                );
                
                setParts({
                    code: code,
                    township: matchedTown ? matchedTown.name_en : rawTownship,
                    type: match[3],
                    number: match[4]
                });
            } else {
                // Fallback for non-standard formats
                const simpleMatch = value.split('/');
                if (simpleMatch.length > 1) {
                    const code = simpleMatch[0];
                    const rest = simpleMatch[1];
                    const townMatch = rest.match(/^([A-Za-z]+)/);
                    const typeMatch = rest.match(/\(([N,E,P,T,R,S])\)/);
                    // Allow 1-6 digits in fallback as well
                    const numMatch = rest.match(/(\d{1,6})$/);
                    
                    const rawTown = townMatch ? townMatch[1] : "YaKaNa";
                    const matchedTown = nrcdata.find(item => 
                        item.nrc_code === code && 
                        item.name_en.toLowerCase() === rawTown.toLowerCase()
                    );

                    setParts({
                        code: code || "12",
                        township: matchedTown ? matchedTown.name_en : rawTown,
                        type: typeMatch ? typeMatch[1] : "N",
                        number: numMatch ? numMatch[1] : ""
                    });
                }
            }
        }
    }, [value]);

    const townships = useMemo(() => {
        return nrcdata.filter(item => item.nrc_code === parts.code);
    }, [parts.code]);

    const updateValue = (newParts) => {
        const p = { ...parts, ...newParts };
        setParts(p);
        const combined = `${p.code}/${p.township}(${p.type})${p.number}`;
        onChange(combined);
    };

    const handlePartChange = (field) => (e) => {
        const val = e.target.value;
        const newParts = { [field]: val };
        
        if (field === 'code') {
            const firstTownship = nrcdata.find(item => item.nrc_code === val);
            if (firstTownship) {
                newParts.township = firstTownship.name_en;
            }
        }
        
        updateValue(newParts);
    };

    const handleNumberChange = (e) => {
        const val = e.target.value.replace(/\D/g, '').slice(0, 6);
        updateValue({ number: val });
    };

    return (
        <Box>
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <FormControl sx={{ minWidth: 70 }}>
                    <InputLabel id="nrc-code-label">State</InputLabel>
                    <Select
                        labelId="nrc-code-label"
                        value={parts.code}
                        label="State"
                        onChange={handlePartChange('code')}
                        size="small"
                        sx={{ borderRadius: 2 }}
                    >
                        {NRC_CODES.map(c => (
                            <MenuItem key={c.en} value={c.en}>{c.en}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Typography sx={{ mt: 1, fontWeight: 'bold', color: '#94a3b8' }}>/</Typography>

                <FormControl sx={{ minWidth: 120, flex: 1 }}>
                    <InputLabel id="nrc-township-label">Township</InputLabel>
                    <Select
                        labelId="nrc-township-label"
                        value={parts.township}
                        label="Township"
                        onChange={handlePartChange('township')}
                        size="small"
                        sx={{ borderRadius: 2 }}
                    >
                        {townships.map(t => (
                            <MenuItem key={t.id} value={t.name_en}>{t.name_en}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 70 }}>
                    <InputLabel id="nrc-type-label">Type</InputLabel>
                    <Select
                        labelId="nrc-type-label"
                        value={parts.type}
                        label="Type"
                        onChange={handlePartChange('type')}
                        size="small"
                        sx={{ borderRadius: 2 }}
                    >
                        {NRC_TYPES.map(t => (
                            <MenuItem key={t.en} value={t.en}>{t.en}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    placeholder="123456"
                    value={parts.number}
                    onChange={handleNumberChange}
                    size="small"
                    error={!!error}
                    sx={{ 
                        flex: 1,
                        '& .MuiOutlinedInput-root': { borderRadius: 2 }
                    }}
                />
            </Stack>
            {helperText && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', ml: 1 }}>
                    {helperText}
                </Typography>
            )}
        </Box>
    );
};

export default NRCSelector;
